import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import API, { getDeviceFingerprint } from '../api';
import { useApp } from '../store';
import { useLang, LangToggle } from '../i18n';

export default function Auth() {
  const [mode, setMode]    = useState('login');
  const [email, setEmail]  = useState('');
  const [code, setCode]    = useState('');
  const [password, setPwd] = useState('');
  const [nickname, setNick]     = useState('');
  const [inviteCode, setInvite] = useState('');
  const [loading, setLoad]      = useState(false);
  const [msg, setMsg]           = useState(null);
  const [sent, setSent]         = useState(false);

  const { login } = useApp();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const code = searchParams.get('invite');
    if (code) { setInvite(code.toUpperCase()); setMode('register'); }
  }, [searchParams]);

  const err = t => setMsg({ type:'error', text:t });
  const ok  = t => setMsg({ type:'ok',    text:t });

  async function sendCode() {
    if (!email) return err('请输入邮箱');
    setLoad(true);
    try {
      const res = await API.post('/api/auth/send-code', { email });
      setSent(true);
      if (res.code) { setCode(res.code); ok(`验证码：${res.code}（已自动填入）`); }
      else ok('验证码已发送，请查收邮件');
    } catch(e) { err(e); }
    finally { setLoad(false); }
  }

  async function handleRegister(e) {
    e.preventDefault();
    if (!sent) return err('请先获取验证码');
    setLoad(true); setMsg(null);
    try {
      const res = await API.post('/api/auth/register', {
        email, code, password, nickname,
        deviceFingerprint: getDeviceFingerprint(),
        inviteCode: inviteCode || undefined,
      });
      login(res.token, {
        ...res,
        id: res.id ?? res.userId,
        email: res.email ?? email,
        nickname: res.nickname ?? nickname,
      });
      nav('/lobby');
    } catch(e) {
      err(e?.message || e || '注册失败');
    }
    finally { setLoad(false); }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setLoad(true); setMsg(null);
    try {
      const res = await API.post('/api/auth/login', {
        email, password,
        deviceFingerprint: getDeviceFingerprint(),
      });
      login(res.token, res);
      nav('/lobby');
    } catch(e) { err(e?.message || e || '登录失败'); }
    finally { setLoad(false); }
  }

  return (
    <div style={{
      minHeight:'100dvh',
      background:'linear-gradient(180deg, #0B1E4A 0%, #0D2B6B 50%, #091840 100%)',
      backgroundAttachment:'fixed',
      display:'flex', alignItems:'center', justifyContent:'center',
      padding:'20px 16px',
    }}>
      {/* 蓝色背景光晕 */}
      <div style={{
        position:'fixed', inset:0, pointerEvents:'none',
        background:'radial-gradient(ellipse at top, rgba(46,127,232,0.2) 0%, transparent 60%)',
      }}/>

      <div className="fade-in" style={{ width:'100%', maxWidth:400, position:'relative' }}>

        {/* 标题区域 */}
        <div style={{ textAlign:'center', marginBottom:36 }}>
          <div style={{ fontSize:28, fontWeight:900, color:'#fff', letterSpacing:0.5 }}>$ANSEM Battle</div>
          <div style={{ fontSize:13, color:'#8BA8D4', marginTop:6, letterSpacing:0.5 }}>
            {t('authSubtitle')}
          </div>
        </div>

        {/* 卡片 */}
        <div style={{
          background:'#0F2554',
          border:'none',
          borderRadius:20, padding:'28px 24px',
          boxShadow:'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 40px rgba(0,0,0,0.5)',
        }}>
          {/* Tab 切换 */}
          <div style={{
            display:'flex', background:'rgba(9,24,64,0.8)', borderRadius:12,
            padding:4, marginBottom:24, gap:4,
          }}>
            {['login','register'].map(m => (
              <button key={m} onClick={() => { setMode(m); setMsg(null); }}
                style={{
                  flex:1, padding:'10px', border:'none', borderRadius:10, cursor:'pointer',
                  background: mode===m ? 'linear-gradient(180deg, #FFD700 0%, #E6A800 100%)' : 'transparent',
                  color: mode===m ? '#000' : '#8BA8D4',
                  fontSize:14, fontWeight:700, fontFamily:'Inter',
                  transition:'all 0.18s',
                  boxShadow: mode===m ? '0 2px 8px rgba(245,197,24,0.4)' : 'none',
                }}>
                {m==='login' ? t('loginTab') : t('registerTab')}
              </button>
            ))}
          </div>

          {/* 提示消息 */}
          {msg && (
            <div style={{
              padding:'11px 14px', borderRadius:10, marginBottom:18, fontSize:13,
              background: msg.type==='error' ? 'rgba(232,69,60,0.1)' : 'rgba(46,204,113,0.1)',
              border:'none',
              color: msg.type==='error' ? '#E8453C' : '#2ECC71',
            }}>{msg.text}</div>
          )}

          {mode==='login' ? (
            <form onSubmit={handleLogin} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <div style={{ fontSize:12, color:'#8BA8D4', marginBottom:6, fontWeight:500 }}>{lang==='en'?'Email':'邮箱地址'}</div>
                <input className="input" placeholder="your@email.com" type="email"
                  value={email} onChange={e=>setEmail(e.target.value)} required />
              </div>
              <div>
                <div style={{ fontSize:12, color:'#8BA8D4', marginBottom:6, fontWeight:500 }}>{t('passwordLabel')}</div>
                <input className="input" placeholder={lang==='en'?'Enter password':'输入密码'} type="password"
                  value={password} onChange={e=>setPwd(e.target.value)} required />
              </div>
              <button className="btn btn-gold" type="submit" disabled={loading}
                style={{ width:'100%', marginTop:4, padding:'16px', fontSize:16 }}>
                {loading ? t('loggingIn') : (lang==='en'?'Enter Battle':'进入战场')}
              </button>
              <p style={{ textAlign:'center', color:'#4A6A9A', fontSize:13 }}>
                {lang==='en'?'No account?':'没有账号？'}
                <span style={{ color:'#F5C518', cursor:'pointer', marginLeft:4, fontWeight:600 }}
                  onClick={()=>{ setMode('register'); setMsg(null); }}>{lang==='en'?'Register':'立即注册'}</span>
              </p>
            </form>
          ) : (
            <form onSubmit={handleRegister} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <div style={{ fontSize:12, color:'#8BA8D4', marginBottom:6, fontWeight:500 }}>{lang==='en'?'Email':'邮箱地址'}</div>
                <input className="input" placeholder="your@email.com" type="email"
                  value={email} onChange={e=>setEmail(e.target.value)} required />
              </div>
              <div>
                <div style={{ fontSize:12, color:'#8BA8D4', marginBottom:6, fontWeight:500 }}>{t('verifyCodeLabel')}</div>
                <div style={{ display:'flex', gap:8 }}>
                  <input className="input" placeholder={lang==='en'?'6-digit code':'6位验证码'}
                    value={code} onChange={e=>setCode(e.target.value)}
                    style={{ flex:1 }} maxLength={6} />
                  <button type="button"
                    onClick={sendCode} disabled={loading||sent}
                    style={{
                      padding:'0 16px',
                      background: sent ? 'rgba(15,37,84,0.9)' : 'linear-gradient(180deg, #3A8EF6 0%, #1A5FBF 100%)',
                      color: sent ? '#4A6A9A' : '#fff',
                      border:'none',
                      borderRadius:12,
                      fontFamily:'Inter', fontSize:13, fontWeight:600,
                      cursor: sent ? 'default' : 'pointer',
                      whiteSpace:'nowrap', flexShrink:0,
                      boxShadow: sent ? 'none' : '0 4px 0 #0F3A80',
                    }}>
                    {sent ? t('codeSent') : t('sendCode')}
                  </button>
                </div>
              </div>
              <div>
                <div style={{ fontSize:12, color:'#8BA8D4', marginBottom:6, fontWeight:500 }}>{t('passwordLabel')}</div>
                <input className="input" placeholder={lang==='en'?'At least 8 chars':'至少8位'} type="password"
                  value={password} onChange={e=>setPwd(e.target.value)} required minLength={8} />
              </div>
              <div>
                <div style={{ fontSize:12, color:'#8BA8D4', marginBottom:6, fontWeight:500 }}>{t('nicknameLabel')}</div>
                <input className="input" placeholder={lang==='en'?'Your game nickname':'你的游戏昵称'}
                  value={nickname} onChange={e=>setNick(e.target.value)} maxLength={20} />
              </div>
              <div style={{marginBottom:16}}>
                <div style={{ fontSize:12, color:'#8BA8D4', marginBottom:6, fontWeight:500 }}>{t('inviteCodeLabel')}</div>
                <input className="input" placeholder={lang==='en'?'Enter invite code':'输入好友邀请码'}
                  value={inviteCode} onChange={e=>setInvite(e.target.value.toUpperCase())}
                  maxLength={8} style={{fontFamily:'Share Tech Mono',letterSpacing:2}} />
              </div>
              <button className="btn btn-gold" type="submit" disabled={loading}
                style={{ width:'100%', marginTop:4, padding:'16px', fontSize:16 }}>
                {loading ? t('registering') : (lang==='en'?'Create Account':'创建账号')}
              </button>
            </form>
          )}
        </div>

        <div style={{position:'absolute',top:0,right:0,padding:'8px'}}>
          <LangToggle />
        </div>
        <p style={{ textAlign:'center', color:'#4A6A9A', fontSize:12, marginTop:20, lineHeight:1.6 }}>
          {lang==='en'?'Virtual points game · Based on real on-chain price · Not investment advice':'纯虚拟积分游戏 · 参考真实链上行情 · 不构成投资建议'}
        </p>
      </div>
    </div>
  );
}
